import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminLogin } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      setError("Please fill in both fields");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const data = await adminLogin(username, password);
      // login via AuthContext to synchronize everything seamlessly
      login({ accessToken: data.accessToken, role: "admin" });
      navigate("/admin");
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        err.response?.data ||
        "Invalid login credentials"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] w-full bg-[#1C1C1E] flex flex-col justify-center items-center font-sans p-5 relative overflow-hidden">
      
      {/* Background Decorators */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#D62422] rounded-full blur-[150px] opacity-20 pointer-events-none"></div>
      
      <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-2xl relative z-10 z-[1] flex flex-col overflow-hidden">
        
        {/* Header Ribbon */}
        <div className="absolute top-0 left-0 w-full h-2 bg-[#D62422]"></div>
        
        <div className="flex items-center gap-3 mb-8 pt-4 justify-center">
            <div className="w-10 h-10 bg-[#D62422] rounded flex items-center justify-center font-bold text-white text-xl shadow-lg">F</div>
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900">Admin<span className="text-[#D62422]">Panel</span></h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-50 border-l-4 border-[#D62422] p-4 text-sm text-[#D62422] rounded shadow-sm">
              <span className="font-semibold">{error}</span>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-bold text-gray-700 ml-1">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your admin username"
              className="w-full h-14 bg-gray-50 border border-gray-200 focus:border-[#D62422] focus:ring-2 focus:ring-[#D62422]/20 rounded-xl px-4 text-gray-900 font-medium outline-none transition-all placeholder:font-normal placeholder:text-gray-400"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-bold text-gray-700 ml-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full h-14 bg-gray-50 border border-gray-200 focus:border-[#D62422] focus:ring-2 focus:ring-[#D62422]/20 rounded-xl px-4 text-gray-900 font-medium outline-none transition-all placeholder:text-gray-400"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-[#D62422] text-white rounded-xl font-bold text-lg tracking-wide uppercase hover:bg-red-700 active:scale-[0.98] transition-all disabled:opacity-70 flex items-center justify-center shadow-lg mt-4 cursor-pointer"
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : "Authenticate"}
          </button>
        </form>
      </div>
      
      <div className="mt-8 text-gray-500 text-sm opacity-80">
        FitQuest Secure Administration &copy; 2026
      </div>
    </div>
  );
}
